import { CoordinatorService } from './coordinator.service';
import { Script } from './script';
import { ScriptItem } from './script-x-item';
import { ValueItem } from './value-item';
import { ValueItemTemplate } from './value-item-template';

type mainTp = 'string' | 'number' | 'boolean'; // | 'date';
type choiceTp = string | number | boolean; // | Date;
type CHOICE = { id: string; label: choiceTp; isSelected?: boolean };
type kindTp = 'value' | 'list' | 'choice' | 'multichoice' | 'formula';

export type VIFPAR = {
    id?: string;
    title?: string;
    description?: string;
    type?: mainTp;
    kind?: kindTp;
    choiceLs?: CHOICE[];
    script?: string;
    isLoad?: boolean;
    editTemplate?: ValueItemTemplate;
};

export class ValueItemField {
    id: string;
    title: string;
    description: string;
    type?: mainTp;
    kind?: kindTp;
    choiceLs?: CHOICE[];
    script?: string;
    private _scriptInstance?: Script;
    // private _editedValueItemChoiceLs: CHOICE[] = [];
    private _editedRadioChoiceId?: string;

    static typeLs: mainTp[] = ['string', 'number', 'boolean']; //'date'
    static kindLs: kindTp[] = ['value', 'list', 'choice', 'multichoice', 'formula'];
    private _editTemplate?;

    private _editMap = new Map<string, any>();
    coords: CoordinatorService;

    constructor(
        public template: ValueItemTemplate,
        { id, title, description, type, kind, choiceLs, script, editTemplate }: VIFPAR,
    ) {
        this.coords = template.coords;
        this.id = id || this.coords.uniqueId();
        this.title = title || '';
        this.description = description || '';
        this.type = type || 'string';
        this.kind = kind || 'value';
        this.choiceLs = choiceLs;
        this.script = script;
        this._compileScript();
        this._editTemplate = editTemplate;
    }

    get radioEditChoice() {
        return this._editedRadioChoiceId || '';
    }

    set radioEditChoice(choiceId: string) {
        this._editedRadioChoiceId = choiceId;
        this._markForCheck();
    }

    resetRadioEditChoice() {
        this._editedRadioChoiceId = undefined;
    }

    get hasDetail() {
        return ['choice', 'multichoice', 'formula'].includes(this.editKind);
    }

    toggleEditDescription() {
        this.showEditDescription = !this.showEditDescription;
        this._markForCheck();
    }

    value(vitm: ValueItem, position: number) {
        const v = vitm?.getValue(this, position);
        switch (this.kind) {
            case 'choice':
                if (v && v.length)
                    for (const ch of this.choiceLs || []) {
                        if (ch.id === v[0]) return ch.label;
                    }
                break;
            case 'multichoice':
                if (v && v.length) {
                    return (this.choiceLs || [])
                        .filter((ch) => v.includes(ch.id))
                        .map((ch) => ch.label)
                        .join('; ');
                }
                break;
        }

        if (this.type === 'boolean') return v ? 'Yes' : 'No';
        return v || '—';
    }

    editedValue(vitm: ValueItem, position: number) {
        let v = vitm?.getValue(this, position);

        switch (this.kind) {
            case 'value':
                v === undefined && (v = '');
                break;
            case 'choice':
                v === undefined && (v = []);
                if (v.length) {
                    for (const ch of this.choiceLs || []) {
                        ch.id === v[0] && (this._editedRadioChoiceId = ch.id);
                    }
                }
                break;
            case 'multichoice':
                v = this.choiceLs!.map((ch) => ({
                    id: ch.id,
                    label: ch.label,
                    isSelected: v?.includes(ch.id),
                }));
                break;
        }

        return v;
    }

    setValue(vitm: ValueItem, value: any, position: number) {
        vitm?.setValue(this, value, position);
    }

    get editTitle() {
        return this._editMap.get('editTitle') || '';
    }

    set editTitle(v: string) {
        this._editMap.set('editTitle', v);
        this._editTemplate!.updateEditLs();
    }

    get editDescription() {
        return this._editMap.get('editDescription') || '';
    }

    set editDescription(v: string) {
        this._editMap.set('editDescription', v);
    }

    get editType(): mainTp {
        return this._editMap.get('editType') || 'string';
    }

    set editType(v: string) {
        this._editMap.set('editType', v);
    }

    get editKind(): kindTp {
        return this._editMap.get('editKind') || 'value';
    }

    set editKind(v: string) {
        this._editMap.set('editKind', v);
    }

    get showEditDescription() {
        return this._editMap.has('showEditDescription');
    }

    set showEditDescription(v: boolean) {
        if (v) this._editMap.set('showEditDescription', true);
        else this._editMap.delete('showEditDescription');
    }

    get editChoiceLs() {
        return this._editMap.get('editChoiceLs') || [];
    }

    confirmEditedChoice(ch: any, e: Event) {
        e.preventDefault();
        e.stopPropagation();
        ch.isEdited = false;
        this._markForCheck();
    }

    addChoice(label: choiceTp) {
        let ls = this._editMap.get('editChoiceLs');
        if (!ls) {
            ls = [];
            this._editMap.set('editChoiceLs', ls);
        }
        for (let v of ls) {
            if (v.value === label) return;
        }
        ls.push({ label, isEdited: true, id: this.coords.uniqueId() });
        this._markForCheck();
    }

    editChoice(ch: any) {
        let ls: { id: string }[] = this._editMap.get('editChoiceLs')!;
        ls.forEach((origCh: any) => (origCh.isEdited = origCh === ch));
        this._markForCheck();
    }

    deleteChoice(ch: any) {
        let ls: { id: string }[] = this._editMap.get('editChoiceLs')!;
        ls = ls.filter((v) => v !== ch);
        this._editMap.set('editChoiceLs', ls);
        this._markForCheck();
    }

    plusChoice() {
        this.addChoice('');
        this._markForCheck();
    }

    move(tp: 'up' | 'down') {
        const ls = this._editTemplate!.editFieldLs;
        const i = ls!.indexOf(this);
        const j = tp == 'up' ? i - 1 : i + 1;
        ls?.splice(i, 1);
        ls?.splice(j, 0, this);
        this._markForCheck();
    }

    isDisabledMove(tp: 'up' | 'down') {
        const ls = this._editTemplate!.editFieldLs;
        return (tp == 'up' && ls[0] === this) || (tp == 'down' && ls[ls.length - 1] === this);
    }

    initEditMap() {
        this._editMap.clear();
        this._editMap.set('editTitle', this.title);
        this._editMap.set('editDescription', this.description);
        this._editMap.set('editType', this.type);
        this._editMap.set('editKind', this.kind);

        this.choiceLs &&
            this._editMap.set(
                'editChoiceLs',
                this.choiceLs.map((ch) => ({
                    label: ch.label,
                    isEdited: false,
                    id: ch.id,
                })),
            );
    }

    private _markForCheck() {
        this._editTemplate!.editCheck$.next();
    }

    submitEdit() {
        this.title = this._editMap.get('editTitle')?.trim() || '';
        this.description = this._editMap.get('editDescription')?.trim() || '';
        this.type = this._editMap.get('editType') || 'string';
        this.kind = this._editMap.get('editKind') || 'value';
        if (this.kind === 'choice' || this.kind === 'multichoice') {
            const choiceLs = this._editMap.get('editChoiceLs');
            this.choiceLs = (choiceLs?.filter((v: any) => v.label.trim() !== '') || []).map(
                (ch: any) => ch,
            );
        }
    }

    submitItemEdit(vitm: ValueItem, value: any, position: number) {
        switch (this.kind) {
            case 'choice':
                value = [this._editedRadioChoiceId].filter((v) => v);
                break;
            case 'multichoice':
                value = value.filter((ch: any) => ch.isSelected).map((ch: any) => ch.id);
                break;
        }
        this.setValue(vitm, value, position);
    }

    formulaEditor() {
        this.coords.display$.dialog$.next({
            action: 'formula-editor',
            target: this,
            backFun: () => this.coords.valueItem$.editDialog(this.template),
        });
    }

    conditionEditor() {
        this.coords.condition$.formulaEditor(this, () =>
            this.coords.valueItem$.editDialog(this.template),
        );
    }

    private _collectionXItemSetWithValues() {
        const s = new Set<string>();

        this.coords.valueItem$.valueItemLs.forEach((vitm) => {
            if (vitm.templateId !== this.template.id) return;
            if (vitm.hasValue(this)) {
                s.add(vitm.itemId);
                return;
            }
        });
        return s;
    }

    get count() {
        if (this.kind === 'formula') return this.coords.text('Formula');
        return this._collectionXItemSetWithValues().size;
    }

  
    preview(isAll = false) {
        const stg = this.coords.stage$.current!.tag;

        this.coords.resultList$.activateSubset({
            idSet: this._collectionXItemSetWithValues(),
            title: this.coords.text('valueItems'),
            prefix: this.title,
            icon: 'value',
            class: 'selection-in-general',
            back: () => this.coords.stage$.set(stg),
            stage: 'collection',
        });
    }

    getScript() {
        return this.script || '';
    }

    submitScript(data: string) {
        this.script = data;
        this._compileScript();
        this._markForCheck();
    }

    private _compileScript() {
        if (!this.script?.trim()) return;
        this._scriptInstance = new Script(this.coords, {
            source: this.script,
        });
    }

    scriptValue({
        item,
        position,
        valueItem,
    }: {
        item: ScriptItem;
        position: number;
        valueItem: ValueItem;
    }) {
        if (!this.script) return '';
        const tables = valueItem.scriptTableLs;
        const currentTable = tables[position];
        try {
            return this._scriptInstance?.run({ item, tables, currentTable });
        } catch (error: any) {
            return `<b style='color: red'>Error: ${error.message}</b>`;
        }
        // return this.coords.script$.run(this.script);
    }

    save() {
        const dct: any = {
            id: this.id,
            title: this.title,
            description: this.description,
            type: this.type,
            kind: this.kind,
        };
        this.choiceLs && this.choiceLs.length && (dct.choiceLs = this.choiceLs);
        this.script && (dct.script = this.script);
        return dct;
    }

    delete() {
        this._editMap.set('isDeleted', true);
        this._editTemplate?.editCheck$.next();
    }

    get isEditDeleted() {
        return this._editMap.has('isDeleted');
    }
}
